import { Directive, ElementRef, OnDestroy, afterNextRender, inject } from '@angular/core';

/**
 * Mantiene el elemento en el top layer del navegador (Popover API) para que
 * quede por encima de los overlays del CDK (diálogos, tooltips…), que también
 * viven en el top layer y se apilan por orden de aparición ignorando z-index.
 * Mismo mecanismo que dev-bottom-bar: se muestra una vez y se re-muestra cada
 * vez que el CDK añade un overlay nuevo, volviendo así a lo alto de la pila.
 *
 * El elemento no cambia de sitio en el DOM (bindings y :hover intactos), pero
 * pasa a pintarse en fixed: la directiva lo recoloca sobre el rect de su padre
 * en cada resize/scroll/mutación del nav/transición del sidenav. Sin Popover
 * API se queda como estaba (posicionado por CSS dentro de la fila).
 */
@Directive({
  selector: '[appTopLayerAction]',
  standalone: true,
  host: { popover: 'manual' }
})
export class TopLayerActionDirective implements OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);

  private navObserver: MutationObserver | null = null;
  private overlayObserver: MutationObserver | null = null;
  private bodyObserver: MutationObserver | null = null;
  private sidenav: Element | null = null;
  private readonly timeouts: number[] = [];
  private readonly sync = () => this.position();

  constructor() {
    afterNextRender(() => this.init());
  }

  private init(): void {
    const host = this.el.nativeElement;
    if (typeof host.showPopover !== 'function') {
      // Sin soporte, el atributo popover es inerte: no hay que hacer nada.
      return;
    }
    try {
      host.showPopover();
    } catch {
      // No se pudo entrar al top layer: quitar el atributo restaura el
      // display normal (el UA oculta los popover cerrados).
      host.removeAttribute('popover');
      return;
    }

    this.position();
    // Reposicionamientos tardíos: fuentes e items del nav que llegan async.
    for (const ms of [250, 1000, 2500]) {
      this.timeouts.push(window.setTimeout(this.sync, ms));
    }

    window.addEventListener('resize', this.sync, { passive: true });
    document.addEventListener('scroll', this.sync, { capture: true, passive: true });
    this.sidenav = host.closest('mat-sidenav');
    this.sidenav?.addEventListener('transitionend', this.sync);

    const navList = host.closest('mat-nav-list');
    if (navList) {
      this.navObserver = new MutationObserver(this.sync);
      this.navObserver.observe(navList, { childList: true, subtree: true });
    }

    const container = document.querySelector('.cdk-overlay-container');
    if (container) {
      this.watchOverlays(container);
    } else {
      // El CDK crea el contenedor de overlays de forma perezosa.
      this.bodyObserver = new MutationObserver(() => {
        const created = document.querySelector('.cdk-overlay-container');
        if (created) {
          this.bodyObserver?.disconnect();
          this.bodyObserver = null;
          this.watchOverlays(created);
        }
      });
      this.bodyObserver.observe(document.body, { childList: true });
    }
  }

  /** Cada overlay nuevo del CDK entra encima en el top layer: re-mostrarse
   *  devuelve este elemento a lo alto de la pila. */
  private watchOverlays(container: Element): void {
    const host = this.el.nativeElement;
    this.overlayObserver = new MutationObserver(() => {
      // Diferido para correr DESPUÉS del showPopover() del CDK sobre el nuevo overlay.
      setTimeout(() => {
        try {
          host.hidePopover();
          host.showPopover();
        } catch { /* estado de popover cambiado por debajo — el siguiente evento resincroniza */ }
        this.position();
      });
    });
    this.overlayObserver.observe(container, { childList: true });
  }

  /** Recoloca el elemento (fixed) sobre el borde derecho de su fila padre,
   *  centrado en vertical; oculto si la fila no se está viendo. */
  private position(): void {
    const host = this.el.nativeElement;
    const row = host.parentElement;
    if (!row || !host.matches(':popover-open')) {
      return;
    }
    const rect = row.getBoundingClientRect();
    const hidden = rect.width === 0 || getComputedStyle(row).visibility === 'hidden';
    host.style.visibility = hidden ? 'hidden' : '';
    if (hidden) {
      return;
    }
    host.style.left = `${rect.right - 16 - host.offsetWidth}px`;
    host.style.top = `${rect.top + (rect.height - host.offsetHeight) / 2}px`;
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.sync);
    document.removeEventListener('scroll', this.sync, { capture: true } as EventListenerOptions);
    this.sidenav?.removeEventListener('transitionend', this.sync);
    this.timeouts.forEach(id => window.clearTimeout(id));
    this.navObserver?.disconnect();
    this.overlayObserver?.disconnect();
    this.bodyObserver?.disconnect();
    try {
      this.el.nativeElement.hidePopover();
    } catch { /* no estaba mostrado */ }
  }
}
